import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Form } from "antd";
import { useForm } from "antd/es/form/Form";
import PolicySettingsPage from "pages/settings/policy/PolicySettingsPage";
import { PolicySection } from "pages/settings/policy/policySection";
import { cancellationPolicy } from "pages/settings/policy/CancellationPolicySection";
import { waitlistPolicy } from "pages/settings/policy/WaitlistPolicySection";
import { visibilityPolicy } from "pages/settings/policy/VisibilityWindowSection";
import { scheduleConflictPolicy } from "pages/settings/policy/ScheduleConflictPolicySection";
import { autoCompletePolicy } from "pages/settings/policy/AutoCompletePolicySection";
import { validityStartPolicy } from "pages/settings/policy/ValidityStartPolicySection";
import { translate } from "shared/languages";
import { AdminCapability } from "shared/utils/capabilities";

// Pengaturan -> Kebijakan Booking & Jadwal: the six booking and product rules
// on one page, one section each, saved together. They were six pages of one
// or two settings each until 2026-10-07, which read as mostly empty space;
// each rule's old route now redirects to its section (#key).
//
// One Save for the page, like Notifikasi and Pembatasan Admin: the confirm
// lists every changed rule with that rule's own note, and only the rules that
// changed are sent. Every rule sits under the same Pembatasan Admin toggle
// (booking_policy), so the page is read-only as a whole or not at all.
const SECTIONS: PolicySection[] = [
  cancellationPolicy,
  waitlistPolicy,
  visibilityPolicy,
  scheduleConflictPolicy,
  autoCompletePolicy,
  validityStartPolicy,
];

const BookingPoliciesPage = () => {
  const [form] = useForm();
  const { hash } = useLocation();
  // Defined once the shell has hydrated the form. Sections grow then (a
  // switch that is on shows its extra rows), so scrolling any earlier lands
  // short of the section.
  const hydrated = Form.useWatch("schedule_conflict_prevention_enabled", form) !== undefined;

  // Opened from an old per-rule link: scroll to that rule's section.
  useEffect(() => {
    if (!hydrated || !hash) return;
    const frame = requestAnimationFrame(() =>
      document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start" }),
    );
    return () => cancelAnimationFrame(frame);
  }, [hydrated, hash]);

  return (
    <PolicySettingsPage
      sectioned
      capability={AdminCapability.BookingPolicy}
      title={translate("class_settings.booking_policies.title")}
      form={form}
      initialValues={(s) => Object.assign({}, ...SECTIONS.map((p) => p.initialValues(s)))}
      buildSave={(v, s) => {
        const changed = SECTIONS.map((p) => p.buildSave(v, s)).filter((p) => p.rows.length > 0);
        return {
          rows: changed.flatMap((p) => p.rows),
          payload: Object.assign({}, ...changed.map((p) => p.payload)),
          // Waitlist and visibility share a note; say it once. A rule whose
          // warning says it all (waitlist switched off) has none.
          notes: Array.from(new Set(changed.flatMap((p) => (p.note ? [p.note] : [])))),
          warnings: changed.flatMap((p) => (p.warning ? [p.warning] : [])),
        };
      }}
    >
      {SECTIONS.map(({ key, Fields }) => (
        <Fields key={key} form={form} />
      ))}
    </PolicySettingsPage>
  );
};

export default BookingPoliciesPage;
